"use client";

import { useEffect, useRef, useState } from "react";
import { Clock, FileText } from "lucide-react";
import { Progress } from "@/components/ui/progress";
export default function TheVelvetExperience() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  const steps = [{
    number: "01",
    title: "CONSULTATION",
    description: "Every transformation begins with a private consultation in our studio. We analyze your hair texture, face shape, and lifestyle goals to curate a bespoke look. Our stylists translate your vision into a detailed plan that ensures every cut and color choice enhances your natural beauty.",
    duration: "30 MINS",
    deliverable: "STYLE BLUEPRINT",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_consultation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_consultation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_consultation.png-wvc-sizes",
    alt: "Stylist discussing hair goals with a client"
  }, {
    number: "02",
    title: "COLOR MAPPING",
    description: "We utilize advanced color theory to map your hair's undertones and desired vibrancy. Whether it is a soft balayage or a bold transformation, we record every section and saturation point. This precise map becomes the foundation for your custom color application.",
    duration: "2 HOURS",
    deliverable: "COLOR FORMULA",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_color_mapping.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_color_mapping.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_color_mapping.png-wvc-sizes",
    alt: "Colorist applying custom balayage highlights"
  }, {
    number: "03",
    title: "PRECISION CUT",
    description: "Inside our styling suite, we execute the cut with surgical precision. Every angle and layer is crafted to ensure movement, volume, and ease of maintenance. We iterate on the shape until the silhouette perfectly complements your features and hair density.",
    duration: "1 HOUR",
    deliverable: "BESPOKE SILHOUETTE",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_precision_cut.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_precision_cut.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_precision_cut.png-wvc-sizes",
    alt: "Stylist performing a precision haircut"
  }, {
    number: "04",
    title: "TREATMENT RITUAL",
    description: "Our deep-conditioning treatments are designed to restore health and luster to every strand. We select premium botanical infusions tailored to your hair's specific needs, ensuring that the final result is not just beautiful, but deeply nourished and resilient.",
    duration: "45 MINS",
    deliverable: "REJUVENATED HAIR",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_treatment.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_treatment.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_treatment.png-wvc-sizes",
    alt: "Client receiving a luxury deep-conditioning treatment"
  }, {
    number: "05",
    title: "FINISHING TOUCH",
    description: "The final styling session brings your look to life. We provide a professional blowout and teach you the techniques to maintain your style at home. You depart with a personalized care guide and the confidence of a salon-perfect finish every day.",
    duration: "30 MINS",
    deliverable: "FINISHED LOOK",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_finishing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_finishing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_finishing.png-wvc-sizes",
    alt: "Finished hairstyle with a professional blowout"
  }];
  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return;
    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;
      const scrollableDistance = sectionHeight - viewportHeight;
      if (rect.top <= 0 && rect.bottom >= viewportHeight) {
        const scrolled = Math.abs(rect.top);
        const progress = Math.min(Math.max(scrolled / scrollableDistance, 0), 1);
        const totalTranslate = track.scrollWidth - window.innerWidth;
        track.style.transform = `translate3d(-${progress * totalTranslate}px, 0, 0)`;
        const stepIndex = Math.min(Math.floor(progress * steps.length), steps.length - 1);
        setActiveStep(stepIndex);
        setProgressValue(progress * 100);
      } else if (rect.top > 0) {
        track.style.transform = `translate3d(0, 0, 0)`;
        setActiveStep(0);
        setProgressValue(0);
      } else if (rect.bottom < viewportHeight) {
        const totalTranslate = track.scrollWidth - window.innerWidth;
        track.style.transform = `translate3d(-${totalTranslate}px, 0, 0)`;
        setActiveStep(steps.length - 1);
        setProgressValue(100);
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [steps.length]);
  return <section data-section-id="3064" id="the-velvet-experience" className="dark bg-background text-foreground">
      {}
      <div className="lg:hidden px-6 py-24 sm:py-32">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="font-mono text-xs sm:text-sm tracking-widest mb-8 text-primary">
            // OUR PROCESS
          </p>
          <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight mb-8">
            Five steps to{" "}
            <span className="font-serif italic font-normal">
              radiant hair.
            </span>
          </h2>
          <p className="font-default text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto">
            A curated salon protocol designed to elevate your natural beauty. 
            Each phase is a step toward a bespoke transformation that leaves 
            you feeling confident and renewed.
          </p>
        </div>

        <div className="space-y-12">
          {steps.map((step, i) => <div key={step.number} data-index={i} className="relative overflow-hidden">
              <div className="absolute -top-8 -left-2 font-default font-bold leading-none pointer-events-none select-none text-foreground/10" style={{
            fontSize: "clamp(8rem, 30vw, 16rem)"
          }}>
                {step.number}
              </div>

              <div className="relative backdrop-blur-sm bg-card border border-border p-6 sm:p-8 shadow-lg">
                <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-48 sm:h-56 object-cover mb-6" />
                <h3 className="font-default font-bold text-2xl sm:text-3xl tracking-tight mb-4">
                  {step.title}
                </h3>
                <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
                  {step.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 font-mono text-xs tracking-widest">
                    <Clock className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">DURATION:</span>
                    <span>{step.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 font-mono text-xs tracking-widest">
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">DELIVERABLE:</span>
                    <span>{step.deliverable}</span>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {}
      <div ref={sectionRef} className="hidden lg:block relative" style={{
      height: `${steps.length * 100}vh`
    }}>
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          {}
          <div ref={trackRef} className="absolute top-0 left-0 h-full flex will-change-transform" style={{
          width: `${(steps.length + 1) * 100}vw`
        }}>
            {}
            <div className="w-screen h-screen flex items-center justify-center px-12 xl:px-24">
              <div className="max-w-3xl text-center">
                <p className="font-mono text-sm tracking-widest mb-10 text-primary">
                  // OUR PROCESS
                </p>
                <h2 className="font-default font-bold text-7xl xl:text-[6rem] leading-[0.9] tracking-tight mb-10">
                  Five steps to{" "}
                  <span className="font-serif italic font-normal">
                    radiant hair.
                  </span>
                </h2>
                <p className="font-default text-xl xl:text-2xl text-muted-foreground leading-relaxed max-w-[600px] mx-auto">
                  A curated salon protocol designed to elevate your natural beauty. 
                  Each phase is a step toward a bespoke transformation that leaves 
                  you feeling confident and renewed.
                </p>
              </div>
            </div>

            {}
            {steps.map((step, i) => <div key={step.number} data-index={i} className="w-screen h-screen relative flex items-center px-12 xl:px-24 overflow-hidden">
                {}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 font-default font-bold leading-none pointer-events-none select-none text-foreground/10 -ml-4" style={{
              fontSize: "20rem"
            }}>
                  {step.number}
                </div>

                {}
                <div className="relative ml-auto w-full max-w-2xl xl:max-w-3xl backdrop-blur-sm bg-card border border-border p-10 xl:p-12 shadow-lg">
                  <div className="flex gap-8 mb-8">
                    <div className="flex-1">
                      <p className="font-mono text-xs tracking-widest text-primary mb-4">
                        STEP {step.number} / 05
                      </p>
                      <h3 className="font-default font-bold text-4xl xl:text-[2.5rem] leading-tight tracking-tight mb-6">
                        {step.title}
                      </h3>
                    </div>
                    <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-32 h-32 xl:w-40 xl:h-40 object-cover flex-shrink-0" />
                  </div>
                  <p className="font-default text-lg xl:text-xl text-muted-foreground leading-relaxed mb-8">
                    {step.description}
                  </p>
                  <div className="flex flex-wrap gap-6 pt-6 border-t border-border">
                    <div className="flex items-center gap-2 font-mono text-xs tracking-widest">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">DURATION:</span>
                      <span>{step.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-xs tracking-widest">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">
                        DELIVERABLE:
                      </span>
                      <span>{step.deliverable}</span>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>

          {}
          <div className="absolute bottom-0 left-0 right-0 px-12 xl:px-24 pb-8 pointer-events-none">
            <div className="flex items-center gap-6 mb-4">
              <span className="font-mono text-xs tracking-widest text-muted-foreground">
                PROGRESS
              </span>
              <span className="font-mono text-xs tracking-widest text-primary">
                {String(Math.min(activeStep + 1, steps.length)).padStart(2, "0")}{" "}
                / {String(steps.length).padStart(2, "0")}
              </span>
              <span className="font-mono text-xs tracking-widest text-muted-foreground ml-auto">
                {activeStep >= 0 && activeStep < steps.length ? steps[activeStep].title : ""}
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {steps.map((step, i) => <div key={step.number} data-index={i} className="relative h-[2px] bg-border overflow-hidden">
                  <div className="absolute inset-y-0 left-0 transition-all duration-300" style={{
                width: i < activeStep ? "100%" : i === activeStep ? `${(progressValue - i / steps.length * 100) / (100 / steps.length) * 100}%` : "0%",
                backgroundColor: "var(--primary)",
                boxShadow: i === activeStep ? "0 0 12px var(--primary)" : "none"
              }} />
                </div>)}
            </div>
            <div className="mt-4">
              <Progress value={progressValue} className="h-[1px] bg-border rounded-md sr-only" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}